import React, { useEffect, useState } from 'react';
import { useTenant } from '../../../context/TenantContext';
import type { MarketingBrowse } from '../../../context/TenantContext';
import { parseEm } from '../inlineEm';
import { useMarketingCourses } from '../MarketingData';
import { PublicCourseCard } from '../../PublicCourseCard';
import { PublicBundleCard } from '../../PublicBundleCard';
import { publicService, type PublicBundle } from '../../../services/publicService';
import { reportError } from '../../../lib/errorHandling';
import { uniformBadgeFlags } from '../../../lib/cardBadges';
import { CtaLink } from './links';
import { BROWSE, BUNDLES, IEB_PAGE, isFreePracticeLab } from './content';
import { isIebTitle } from './iebLabs';
import { REVISE_ANCHORS } from './sectionMeta';

/**
 * Windows into `/courses`, on the landing page.
 *
 * Both blocks render the catalogue's OWN cards (`PublicCourseCard`,
 * `PublicBundleCard`) in the catalogue's own grid classes. That is the point:
 * someone should recognise the shelf they are about to land on. Cards drawn to
 * match this dark theme would also be a second design of the same objects,
 * free to drift every time a card gains a field.
 *
 * Those cards are light, so each block sits on a light panel — the same move
 * the demo lesson makes, and the reason none of them read as stranded light
 * elements on a dark page.
 *
 * The two windows share `Window` below so "the same, but bundles" stays true
 * as either changes.
 */

/** The light panel, its head and its optional closing button. */
const Window: React.FC<{
  anchor?: string;
  accent: 'study' | 'essay' | 'practice';
  /** Which catalogue card this window holds. The two are styled differently —
   *  white vs `bg-paper-dark` — and the landing surface overrides their titles,
   *  so the CSS has to know which it is looking at. */
  cards: 'courses' | 'bundles';
  /** Run on from the section above instead of opening with the standard rule:
   *  every `.rv-sec` carries a `border-top`, which is the page's way of saying
   *  "new argument starts here". The courses window is the tail of the Practice
   *  Labs section — the same six papers it has just walked through — so the rule
   *  announced a second section where there is only one. */
  joined?: boolean;
  copy: MarketingBrowse;
  children: React.ReactNode;
}> = ({ anchor, accent, cards, joined, copy, children }) => (
  <section
    className={`rv-sec rv-browse${joined ? ' rv-sec-joined' : ''}`}
    id={anchor}
    data-accent={accent}
  >
    <div className="rv-wrap">
      <div className="rv-head">
        {copy.eyebrow && <p className="rv-kicker">{copy.eyebrow}</p>}
        <h2>{parseEm(copy.heading ?? '')}</h2>
        {copy.lede && <p className="rv-lede">{copy.lede}</p>}
      </div>

      <div className="rv-browse-window" data-cards={cards}>
        {/* The catalogue's grid classes verbatim, so each window matches the
            page it is a window into at every breakpoint. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {children}
        </div>
      </div>

      {copy.ctaLabel && copy.ctaHref && (
        <div className="rv-ctas rv-sec-cta">
          <CtaLink className="rv-btn rv-btn-primary" href={copy.ctaHref}>
            {copy.ctaLabel}
          </CtaLink>
        </div>
      )}
    </div>
  </section>
);

/**
 * The six free NSC papers.
 *
 * The data costs nothing: `MarketingDataProvider` wraps every marketing route
 * (see components/MarketingLayout.tsx), so the courses are already in context
 * — `LabSection` reads the same list to link its topic rows. No fetch here.
 */
export const ReviseBrowseWindow: React.FC = () => {
  const { tenant } = useTenant();
  const { courses, loaded } = useMarketingCourses();
  const copy = { ...BROWSE, ...tenant?.branding.marketing?.browse };

  /* Sorted by title, which puts them in paper-and-question order because that
     is how the titles are written ("Practice Lab: Paper 1 | Q1 | …"). The
     catalogue's own order is `total_students` descending, which is right for a
     shelf you are browsing and wrong for a set of six numbered papers — it
     came out Q1, Q3, Q2 and looked shuffled. It would also have re-ordered
     itself as students enrolled. */
  const papers = courses
    .filter((c) => isFreePracticeLab(c.title))
    .sort((a, b) => a.title.localeCompare(b.title));

  /* Nothing to show is a reason to render nothing, not to render a heading over
     an empty grid. `loaded` matters separately from the count: on first paint
     the list is empty because the fetch has not landed, and a "no courses yet"
     state flashing on a marketing page is worse than the section appearing a
     beat late. */
  if (!loaded || papers.length === 0 || !copy.heading) return null;

  // Every Revise course shares one category and level, so the badges are
  // suppressed by the same uniformity rule the catalogue grids use.
  const { hideCategory, hideLevel } = uniformBadgeFlags(courses);

  return (
    <Window anchor={REVISE_ANCHORS.courses} accent="practice" cards="courses" joined copy={copy}>
      {papers.map((course) => (
        <PublicCourseCard
          key={course.id}
          course={course}
          hideCategory={hideCategory}
          hideLevel={hideLevel}
        />
      ))}
    </Window>
  );
};

/**
 * The bundles, high on the page — what is on offer, before the three sections
 * that argue for it.
 *
 * Unlike the courses window this one fetches: `MarketingDataProvider` carries
 * courses only, and teaching it about bundles would make every marketing route
 * of every tenant pay for a query this one block needs. Guarded on the
 * `bundles` flag and best-effort on failure, the same shape PublicCatalog uses
 * — a shelf that fails to load should cost the page a section, not break it.
 */
/**
 * Which board's packs this window is for.
 *
 * One component, two shelves: the query is the same (every published bundle
 * in the tenant) and only the half it keeps differs, so a second copy would
 * be a second thing to fix the next time a card gains a field.
 *
 * `nsc` also carries the tenant's branding override; `ieb` does not, for the
 * reason the rest of IEB_PAGE doesn't — its copy has to agree with a printed
 * flyer.
 */
export const ReviseBundleWindow: React.FC<{ board?: 'nsc' | 'ieb' }> = ({ board = 'nsc' }) => {
  const { tenant, tenantId, features } = useTenant();
  /* The rows are stamped with the tenant they were fetched for. Without that,
     a tenant change leaves the previous tenant's packs on screen while the next
     request is in flight — and permanently if that request fails. */
  const [result, setResult] = useState<{ tenantId: string | null; bundles: PublicBundle[] } | null>(
    null,
  );
  const copy =
    board === 'ieb'
      ? IEB_PAGE.bundles
      : { ...BUNDLES, ...tenant?.branding.marketing?.bundles };

  useEffect(() => {
    if (!features.bundles) {
      setResult({ tenantId, bundles: [] });
      return;
    }
    let cancelled = false;
    setResult(null);
    publicService
      .getPublicBundles(tenantId || undefined)
      .then((bundles) => {
        if (!cancelled) setResult({ tenantId, bundles });
      })
      .catch((err) => {
        // Best-effort: no bundles is a page without this section, which is the
        // same page every tenant without bundles already sees. Reported rather
        // than swallowed so the degradation is visible instead of silent.
        reportError('revise.bundleWindow', err, { tenantId });
        if (!cancelled) setResult({ tenantId, bundles: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, features.bundles]);

  /* Split by board. The query returns every published bundle in the tenant,
     so without this the first IEB pack published turned up under "Buy by the
     paper" on the NSC landing page beside the NSC ones. */
  const bundles = (result?.tenantId === tenantId ? result.bundles : []).filter((b) =>
    board === 'ieb' ? isIebTitle(b.title) : !isIebTitle(b.title),
  );

  if (!result || bundles.length === 0 || !copy.heading) return null;

  return (
    <Window
      anchor={board === 'ieb' ? 'sets' : REVISE_ANCHORS.bundles}
      accent="essay"
      cards="bundles"
      copy={copy}
    >
      {bundles.map((bundle) => (
        <PublicBundleCard key={bundle.id} bundle={bundle} canSell={features.sell_courses} />
      ))}
    </Window>
  );
};
